import React, { useEffect, useState, useMemo, useRef } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { useBuildingTypes } from '../../../shared/hooks/useBuildingTypes';
import { Batiment } from '../../../shared/types/game';
import BuildingDetailModal from './BuildingDetailModal';

interface BuildingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  buildings: Batiment[];
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
  onBuildingsChange?: (buildings: Batiment[]) => void;
}

// Icône PNG par type (fallback si BDD ne fournit qu'un emoji)
const getBuildingIconPath = (buildingType: string): string => {
  switch (buildingType) {
    case 'pompier': return '/images/feu.png';
    case 'hopital': return '/images/crois.png';
    case 'fdo': return '/images/forces-de-lordre.png';
    case 'prison': return '/images/prison.png';
    case 'cta': return '/images/antenne-cta.png';
    default: return '/images/icon-bat.png';
  }
};

const getBuildingIconConfig = (buildingType: string): { backgroundColor: string; iconColor: string } => {
  switch (buildingType) {
    case 'pompier': return { backgroundColor: '#ef4444', iconColor: '#ffffff' };
    case 'hopital': return { backgroundColor: '#ffffff', iconColor: '#ffffff' };
    case 'fdo': return { backgroundColor: '#1e40af', iconColor: '#ffffff' };
    case 'prison': return { backgroundColor: '#ffffff', iconColor: '#ffffff' };
    case 'cta': return { backgroundColor: '#eab308', iconColor: '#ffffff' };
    default: return { backgroundColor: '#10b981', iconColor: '#ffffff' };
  }
};

// Cache pour les adresses récupérées
const addressCache = new Map<string, string>();

// Fonction pour récupérer l'adresse à partir des coordonnées
const getAddressFromCoordinates = async (lat: number, lng: number): Promise<string | null> => {
  const cacheKey = `${lat},${lng}`;
  
  // Vérifier le cache
  if (addressCache.has(cacheKey)) {
    return addressCache.get(cacheKey) || null;
  }

  try {
    // Utiliser l'API Nominatim d'OpenStreetMap (gratuite, pas de clé API requise)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'OpSecours-Game/1.0' // Requis par Nominatim
        }
      }
    );

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    
    if (data && data.address) {
      // Construire l'adresse de manière lisible
      const address = data.address;
      const parts: string[] = [];
      
      if (address.road) parts.push(address.road);
      if (address.house_number) parts[0] = `${address.house_number} ${parts[0] || ''}`.trim();
      if (address.postcode) parts.push(address.postcode);
      if (address.city || address.town || address.village) {
        parts.push(address.city || address.town || address.village);
      }
      if (address.country) parts.push(address.country);

      const fullAddress = parts.join(', ') || data.display_name;
      
      // Mettre en cache
      addressCache.set(cacheKey, fullAddress);
      
      return fullAddress;
    }

    return data.display_name || null;
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'adresse:', error);
    return null;
  }
};

const BuildingsModal: React.FC<BuildingsModalProps> = ({ isOpen, onClose, buildings, onBuildingUpdate, onBuildingsChange }) => {
  const { t } = usePageTranslation('game', 'Map');
  const { buildingTypes: typesFromApi, loading: loadingTypes } = useBuildingTypes();

  const [activeService, setActiveService] = useState<string>('');
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const [addresses, setAddresses] = useState<Record<string, string>>({});
  const [loadingAddresses, setLoadingAddresses] = useState<Set<string>>(new Set());
  const addressesRef = useRef(addresses);
  const loadingAddressesRef = useRef(loadingAddresses);
  const [selectedBuilding, setSelectedBuilding] = useState<Batiment | null>(null);

  // Premier type sélectionné quand la liste BDD est chargée
  useEffect(() => {
    if (typesFromApi.length > 0 && !activeService) {
      setActiveService(typesFromApi[0].id);
    }
  }, [typesFromApi, activeService]);

  // Initialiser searchQueries pour chaque type chargé
  useEffect(() => {
    if (typesFromApi.length === 0) return;
    setSearchQueries((prev) => {
      const next = { ...prev };
      typesFromApi.forEach((s) => {
        if (next[s.id] === undefined) next[s.id] = '';
      });
      return next;
    });
  }, [typesFromApi]);

  useEffect(() => {
    addressesRef.current = addresses;
    loadingAddressesRef.current = loadingAddresses;
  }, [addresses, loadingAddresses]);

  const services = typesFromApi;

  const filteredBuildings = useMemo(() => {
    if (!activeService) return [];
    const serviceBuildings = buildings.filter((b) => b.type === activeService);
    const query = (searchQueries[activeService] || '').toLowerCase().trim();
    if (!query) return serviceBuildings;
    return serviceBuildings.filter(
      (building) =>
        building.nom.toLowerCase().includes(query) ||
        building.localisation?.toLowerCase().includes(query) ||
        building.description?.toLowerCase().includes(query) ||
        addresses[building.localisation || '']?.toLowerCase().includes(query)
    );
  }, [buildings, activeService, searchQueries, addresses]);

  const handleSearchChange = (serviceId: string, value: string) => {
    setSearchQueries((prev) => ({ ...prev, [serviceId]: value }));
  };

  // Fermer avec la touche Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Empêcher le scroll du body quand la modale est ouverte
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Charger les adresses pour les bâtiments affichés
  useEffect(() => {
    if (!isOpen) return;

    const loadAddresses = async () => {
      for (const building of filteredBuildings) {
        const localisation = building.localisation;
        if (!localisation) continue;

        // Vérifier si on a déjà l'adresse en cache ou si on est en train de la charger
        if (addressesRef.current[localisation] || loadingAddressesRef.current.has(localisation)) continue;

        const [latStr, lngStr] = localisation.split(',');
        const lat = parseFloat(latStr.trim());
        const lng = parseFloat(lngStr.trim());

        if (isNaN(lat) || isNaN(lng)) continue;

        setLoadingAddresses(prev => new Set(prev).add(localisation));
        
        const address = await getAddressFromCoordinates(lat, lng);
        
        if (address) {
          setAddresses(prev => ({
            ...prev,
            [localisation]: address
          }));
        }

        setLoadingAddresses(prev => {
          const newSet = new Set(prev);
          newSet.delete(localisation);
          return newSet;
        });
      }
    };

    loadAddresses();
  }, [isOpen, filteredBuildings]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative z-50 bg-gray-900 rounded-lg shadow-2xl w-full max-w-6xl max-h-[75vh] overflow-hidden border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <svg
              className="w-6 h-6 text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            {t('buildings')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
            aria-label="Fermer"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Tabs : types depuis la BDD */}
        {loadingTypes ? (
          <div className="flex border-b border-gray-700 bg-gray-800/30 px-4 py-3 text-sm text-gray-500">
            {t('contextMenu.loading')}
          </div>
        ) : services.length === 0 ? (
          <div className="flex border-b border-gray-700 bg-gray-800/30 px-4 py-3 text-sm text-gray-500">
            {t('contextMenu.noTypes')}
          </div>
        ) : (
          <div className="flex border-b border-gray-700 bg-gray-800/30">
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() => setActiveService(service.id)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeService === service.id
                    ? 'border-b-2 border-blue-500 text-blue-400 bg-gray-800/50'
                    : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/30'
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <div
                    className="rounded-full flex items-center justify-center border-2 border-white shadow-md text-lg"
                    style={{
                      backgroundColor: getBuildingIconConfig(service.id).backgroundColor,
                      width: '24px',
                      height: '24px',
                    }}
                  >
                    {service.icon && service.icon.length <= 4 ? (
                      <span title={service.name}>{service.icon}</span>
                    ) : (
                      <img src={getBuildingIconPath(service.id)} alt={service.name} className="w-4 h-4" />
                    )}
                  </div>
                  <span>{service.name}</span>
                  <span
                    className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      activeService === service.id ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-700 text-gray-500'
                    }`}
                  >
                    {buildings.filter((b) => b.type === service.id).length}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex flex-col h-[calc(75vh-140px)]">
          {!loadingTypes && services.length > 0 && (
            <>
              {/* Barre de recherche */}
              <div className="p-4 border-b border-gray-700">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={`Rechercher dans ${services.find((s) => s.id === activeService)?.name ?? activeService}...`}
                    value={searchQueries[activeService] ?? ''}
                    onChange={(e) => handleSearchChange(activeService, e.target.value)}
                    className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg
                    className="absolute left-3 top-2.5 w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </>
          )}

          {/* Liste des bâtiments */}
          <div className="flex-1 overflow-y-auto p-4">
            {loadingTypes ? (
              <div className="text-center text-gray-400 py-12">
                <p className="text-lg">{t('contextMenu.loading')}</p>
              </div>
            ) : services.length === 0 ? (
              <div className="text-center text-gray-400 py-12">
                <p className="text-lg mb-2">{t('contextMenu.noTypes')}</p>
                <p className="text-sm">{t('buildingsModal.buildBuildingHint')}</p>
              </div>
            ) : filteredBuildings.length === 0 ? (
              <div className="text-center text-gray-400 py-12">
                <p className="text-lg mb-2">
                  {searchQueries[activeService]
                    ? t('buildingsModal.noResults')
                    : `Aucun ${services.find((s) => s.id === activeService)?.name?.toLowerCase() ?? activeService} pour le moment`}
                </p>
                <p className="text-sm">
                  {searchQueries[activeService]
                    ? 'Essayez de modifier votre recherche'
                    : t('buildingsModal.buildBuildingHint')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredBuildings.map((building) => (
                  <div
                    key={building.id}
                    onClick={() => setSelectedBuilding(building)}
                    className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-blue-500 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-1">
                          {building.nom}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <span className={`px-2 py-1 rounded text-xs ${
                            building.actif
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-gray-700 text-gray-500'
                          }`}>
                            {building.actif ? 'Actif' : 'Inactif'}
                          </span>
                          <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
                            Niveau {building.niveau}
                          </span>
                        </div>
                      </div>
                      <div 
                        className="rounded-full flex items-center justify-center border-2 border-white shadow-md flex-shrink-0"
                        style={{ 
                          backgroundColor: getBuildingIconConfig(building.type).backgroundColor,
                          width: '32px',
                          height: '32px'
                        }}
                      >
                        <img 
                          src={getBuildingIconPath(building.type)} 
                          alt={building.type} 
                          className="w-6 h-6"
                        />
                      </div>
                    </div>
                    
                    {building.localisation && (
                      <div className="mb-2 text-sm text-gray-400">
                        <span className="font-medium">Localisation:</span>{' '}
                        {addresses[building.localisation] ? (
                          <span className="text-gray-300">{addresses[building.localisation]}</span>
                        ) : loadingAddresses.has(building.localisation) ? (
                          <span className="text-gray-500 italic">Chargement de l'adresse...</span>
                        ) : (
                          <span className="text-gray-500">{building.localisation}</span>
                        )}
                      </div>
                    )}
                    
                    {building.description && (
                      <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                        {building.description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500 pt-3 border-t border-gray-700">
                      <div>
                        <span className="font-medium">Capacité:</span> {building.capacite || 0}
                      </div>
                      <div>
                        <span className="font-medium">Postes:</span> {building.postes_occupes}/{building.postes_disponibles + building.postes_occupes}
                      </div>
                      {building.cout !== undefined && building.cout > 0 && (
                        <div>
                          <span className="font-medium">Coût:</span> {building.cout.toLocaleString()} €
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de détails */}
      {selectedBuilding && (
        <BuildingDetailModal
          building={selectedBuilding}
          address={selectedBuilding.localisation ? addresses[selectedBuilding.localisation] : undefined}
          onClose={() => setSelectedBuilding(null)}
          onBuildingUpdate={onBuildingUpdate}
        />
      )}
    </div>
  );
};

export default BuildingsModal;

